/** Core domain types for TWYME */

export type UUID = string;

export type LocaleCode = 'en' | 'ru';

export type PlanVisibility = 'shared' | 'only_me';

export type PlanStatus = 'active' | 'completed' | 'cancelled';

export type AvailabilityStatus = 'busy' | 'free' | 'tentative' | 'none';

export type MemberStatus = 'active' | 'removed';

export type InviteStatus = 'pending' | 'used' | 'expired' | 'revoked';

export type SyncStatus =
  | 'synced'
  | 'syncing'
  | 'offline'
  | 'changes_pending'
  | 'retrying'
  | 'sync_error';

export type PlanViewMode = 'month' | 'list';

export type AppMode = 'demo' | 'production';

export interface Profile {
  id: UUID;
  displayName: string;
  avatarPath: string | null;
  accentColor: string;
  locale: LocaleCode;
  createdAt: string;
  updatedAt: string;
}

export interface Space {
  id: UUID;
  name: string;
  createdBy: UUID;
  createdAt: string;
  updatedAt: string;
}

export interface SpaceMember {
  spaceId: UUID;
  userId: UUID;
  joinedAt: string;
  memberStatus: MemberStatus;
}

export interface SpaceInvite {
  id: UUID;
  spaceId: UUID;
  codeHash: string;
  /** Demo/local only — never store plaintext invites in production DB */
  codeHint?: string;
  expiresAt: string;
  status: InviteStatus;
  createdBy: UUID;
  usedAt: string | null;
  usedBy: UUID | null;
  createdAt: string;
}

export interface Plan {
  id: UUID;
  spaceId: UUID;
  ownerId: UUID;
  title: string;
  description: string;
  /** YYYY-MM-DD or null for month plans without a date */
  planDate: string | null;
  /** HH:mm or null */
  startTime: string | null;
  /** HH:mm or null */
  endTime: string | null;
  isAllDay: boolean;
  availabilityStatus: AvailabilityStatus;
  visibility: PlanVisibility;
  hideDetailsFromPartner: boolean;
  status: PlanStatus;
  /** Restricted palette key; null → fall back to owner accent for display */
  colorKey: PlanColorKey | null;
  /** @deprecated legacy free-form; prefer colorKey */
  categoryColor: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  clientId: UUID;
  clientUpdatedAt: string;
}

export interface MonthFocus {
  id: UUID;
  spaceId: UUID;
  ownerId: UUID;
  yearMonth: string;
  text: string;
  visibility: PlanVisibility;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  clientId: UUID;
  clientUpdatedAt: string;
}

export interface ReminderOffset {
  minutesBefore: number;
}

export interface NotificationPreferences {
  enabled: boolean;
  offsets: number[];
}

export interface BackgroundPreferences {
  imageId: string | null;
  /** Stable Storage path for cross-device own wallpaper; never a Dexie id. */
  storagePath: string | null;
  /** Account-level wallpaper mode (synced via preferences jsonb). */
  source: 'none' | 'own' | 'partner';
  fit: 'cover';
  positionX: number;
  positionY: number;
  dim: number;
  blur: number;
  surfaceOpacity: number;
}

export interface UserPreferences {
  userId: UUID;
  locale: LocaleCode;
  timeFormat: '12h' | '24h';
  weekStartsOn: 0 | 1;
  notifications: NotificationPreferences;
  background: BackgroundPreferences;
  onboardingCompleted: boolean;
  shortcutsHintSeen: boolean;
  updatedAt: string;
}

export interface SessionUser {
  id: UUID;
  email: string;
  emailConfirmed: boolean;
}

export interface AuthSession {
  user: SessionUser;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface SpaceSnapshot {
  space: Space;
  members: SpaceMember[];
  profiles: Profile[];
  invite: SpaceInvite | null;
}

export type EntitlementId =
  | 'shared_planning'
  | 'custom_background_local'
  | 'background_cloud_sync'
  | 'recurring_plans'
  | 'multiple_spaces'
  | 'calendar_integrations'
  | 'change_history'
  | 'extra_widgets'
  | 'extended_themes';

export type PlanTier = 'free' | 'premium';

export interface PlanFilters {
  owners: UUID[] | 'all';
  visibility: PlanVisibility | 'all';
  status: PlanStatus | 'all';
  availability: AvailabilityStatus | 'all';
  query: string;
}

export interface SyncOperation {
  id: UUID;
  entityType: 'plan' | 'month_focus' | 'profile' | 'preferences';
  entityId: UUID;
  action: 'upsert' | 'delete';
  payload: unknown;
  createdAt: string;
  attempts: number;
  /** Short user-facing message (no secrets) */
  lastError: string | null;
  /** Dev / diagnostics detail */
  lastErrorDetail?: string | null;
  errorKind?: 'transient' | 'permanent' | null;
  nextRetryAt?: string | null;
}

export type PlanColorKey =
  | 'indigo'
  | 'violet'
  | 'rose'
  | 'coral'
  | 'amber'
  | 'cyan'
  | 'mint'
  | 'graphite';

export const PLAN_COLOR_KEYS: readonly PlanColorKey[] = [
  'indigo',
  'violet',
  'rose',
  'coral',
  'amber',
  'cyan',
  'mint',
  'graphite',
] as const;

export const PLAN_COLOR_HEX: Record<PlanColorKey, string> = {
  indigo: '#7B8CDE',
  violet: '#9B8AC4',
  rose: '#C27B8A',
  coral: '#D4896A',
  amber: '#C4A46B',
  cyan: '#6BA3C4',
  mint: '#6BB3A0',
  graphite: '#8A9BB8',
};

export const BUSY_PLAN_COLOR_KEY: PlanColorKey = 'graphite';

/** Named owner accents for PlanChip strips (synced via profiles.accent_color). */
export const OWNER_ACCENT_SWATCHES = [
  { id: 'red', hex: '#D16B6B' },
  { id: 'coral', hex: '#D4896A' },
  { id: 'orange', hex: '#D4A06A' },
  { id: 'amber', hex: '#C4A46B' },
  { id: 'green', hex: '#7AAD7E' },
  { id: 'mint', hex: '#6BB3A0' },
  { id: 'cyan', hex: '#6BA3C4' },
  { id: 'blue', hex: '#6B8FD4' },
  { id: 'indigo', hex: '#7B8CDE' },
  { id: 'violet', hex: '#9B8AC4' },
  { id: 'pink', hex: '#C27B9A' },
  { id: 'graphite', hex: '#8A9BB8' },
] as const;

export type OwnerAccentId = (typeof OWNER_ACCENT_SWATCHES)[number]['id'];

/** @deprecated prefer OWNER_ACCENT_SWATCHES — kept for callers expecting hex list */
export const ACCENT_COLORS = OWNER_ACCENT_SWATCHES.map((s) => s.hex);

export const DEFAULT_ACCENT = OWNER_ACCENT_SWATCHES.find((s) => s.id === 'indigo')!.hex;

export const MAX_SPACE_MEMBERS = 2;
